/**
 * Les pièces qui ont un tracé dessiné, par code de modèle.
 *
 * Généré : ne pas éditer à la main. Les fichiers correspondants vivent dans
 * `web/public/icones/traits/` et sont servis tels quels, jamais empaquetés :
 * les cent quarante-cinq pèsent 103 Ko une fois compressés, ce qui doublerait
 * le poids du bundle pour des images que le navigateur sait mettre en cache
 * une par une.
 *
 * Ils servent de MASQUE, pas d'image : la teinte du matériau les peint, comme
 * elle peignait les silhouettes tracées à la main qu'ils remplacent. Le cadre
 * du palier, lui, ne bouge pas.
 *
 * Provenance : game-icons.net, licence CC BY 3.0. Voir
 * `web/public/icones/traits/LISEZ-MOI.md` pour les auteurs.
 *
 * ## Dix-neuf tracés ont été retirés le 18-08-2026
 *
 * Enzo : « certaines icônes n'ont rien à voir avec leur item. Par exemple
 * Harnais des poseurs de bornes, c'est une tête. C'est probablement le cas
 * ailleurs aussi. » Les cent cinquante-six ont donc été rendus et regardés un
 * par un, à 72 puis à 130 pixels.
 *
 * Dix-neuf ne représentaient pas ce qu'ils habillaient : un masque à pointes et
 * un heaume cornu sur des harnais, un **bras robotisé** sur deux pièces
 * différentes, une cape à capuche et un parchemin roulé sur des jambières, une
 * main sur un anneau, une vague sur un bâton et une autre sur des gantelets, un
 * collier sur une ceinture, des bois de cerf sur un arc.
 *
 * Ils retombent sur la silhouette de leur famille, qui est générique mais
 * juste. **Juste et générique vaut mieux que précis et faux.** Les fichiers
 * restent en place : ce qui manque est un dessin qui corresponde, pas le
 * mécanisme, et la liste est consignée dans `05-EN-ATTENTE.md`.
 */
export const TRACES = new Set<string>([
  'epee_ebrechee',
  'epee_fer',
  'epee_dotation',
  'epee_poste',
  'epee_marche',
  'epee_reformee',
  'epee_ligne',
  'epee_tenaille',
  'dague_ebrechee',
  'dague_fer',
  'dague_sacquier',
  'dague_commis',
  'dague_corne',
  'dague_non_inscrits',
  'dague_bas_greffe',
  'hache_cognee',
  'hache_boiseur',
  'hache_fendeur',
  'hache_abattage',
  'hache_remonte',
  'masse_fer',
  'masse_maillet',
  'masse_piqueur',
  'masse_carrier',
  'masse_river',
  'masse_fond_de_taille',
  'masse_cran',
  'baton_bois',
  'baton_perche',
  'baton_jauge',
  'baton_aiguilleur',
  'baton_verse',
  'arc_bois',
  'arc_veilleur',
  'arc_peage',
  'arc_marche',
  'arc_maupas',
  'arbalete_poste',
  'arbalete_veille',
  'arbalete_peage',
  'arbalete_maupas',
  'bouclier_bois',
  'bouclier_planche',
  'bouclier_targe_poste',
  'bouclier_rondache_cloutee',
  'bouclier_carrier',
  'bouclier_convoi',
  'bouclier_marche',
  'bouclier_pavois_ligne',
  'bouclier_cran',
  'bouclier_haute_visee',
  'heaume_coiffe_etoupe',
  'heaume_casque_chantier',
  'heaume_carrier',
  'heaume_boiseur',
  'heaume_poste',
  'heaume_matricule',
  'heaume_bassinet_ligne',
  'heaume_marche',
  'heaume_haute_visee',
  'heaume_palier_treize',
  'plastron_cuir',
  'plastron_etoupe',
  'plastron_lin',
  'plastron_convoi',
  'plastron_carrier',
  'plastron_cercle',
  'plastron_poste',
  'plastron_brigandine_ligne',
  'plastron_reforme',
  'plastron_marche',
  'plastron_basse_taille',
  'plastron_chantier_neuf',
  'brassards_portefaix',
  'brassards_report_rasse',
  'gantelets_mitaines_etoupe',
  'gantelets_carrier',
  'gantelets_haveur',
  'gantelets_charroi',
  'gantelets_scelleur',
  'gantelets_poste',
  'gantelets_marche',
  'gantelets_puits',
  'gantelets_verse',
  'gantelets_basse_taille',
  'ceinture_corde',
  'ceinture_portefaix',
  'ceinture_outils',
  'ceinture_ceinturon_poste',
  'ceinture_sangle_charroi',
  'ceinture_matricule',
  'ceinture_jaugeur',
  'ceinture_neuvieme_jalon',
  'jambieres_chausses_lin',
  'jambieres_carrier',
  'jambieres_boiseur',
  'jambieres_rivees',
  'jambieres_poste',
  'bottes_sabots',
  'bottes_brodequins_chantier',
  'bottes_carrier',
  'bottes_ferres',
  'bottes_convoi',
  'bottes_deux_pas',
  'bottes_ressemelees',
  'bottes_poste',
  'bottes_arpenteur',
  'bottes_onzieme_mille',
  'anneau_jonc_cordier',
  'anneau_commis',
  'anneau_contremaitre',
  'anneau_poinconne',
  'anneau_greffe',
  'anneau_dotation',
  'anneau_chambre',
  'anneau_second_registre',
  'amulette_jeton_paie',
  'amulette_medaille_service',
  'amulette_plaque_matricule',
  'amulette_cote_releve',
  'amulette_chaine_contremaitre',
  'amulette_sous_assise',
  'amulette_contreseing_greffe',
  'amulette_etalon_jauge',
  'amulette_plaque_marche',
  'amulette_sceau_chambre',
  // Matériaux et consommables : ils n’ont pas de famille qui veuille dire
  // quelque chose, seulement un code. C’est par lui qu’on les retrouve.
  'ferraille',
  'acier_repris',
  'bois_de_charroi',
  'cuir_racle',
  'fil_de_bronze',
  'pierre_de_taille',
  'verre_pile',
  'eau_de_suif',
  'onguent',
  'pain_de_route',
])
